import React, { useState, useEffect } from "react";
import { X, Send, Loader2, Building, Calendar, FileText, CheckCircle2 } from "lucide-react";
import { addCalendarDays } from "../../utils/dateUtils";

export default function CreateInvoiceModal({
  isOpen,
  onClose,
  po,
  customer,
  company,
  onSendInvoice,
  loading = false
}) {
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [customerPoNumber, setCustomerPoNumber] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  const getDaysFromPaySchedule = (schedule) => {
    if (!schedule) return 30;
    const lower = schedule.toLowerCase();
    if (lower.includes("receipt") || lower.includes("due on receipt")) return 0;
    const match = schedule.match(/\d+/);
    return match ? parseInt(match[0], 10) : 30;
  };

  useEffect(() => {
    if (po && isOpen) {
      const todayStr = new Date().toISOString().split("T")[0];
      setInvoiceDate(po.invoiceDate || todayStr);

      if (po.dueDate) {
        setDueDate(po.dueDate);
      } else {
        const paySchedule = customer?.paySchedule || "Net 30";
        const days = getDaysFromPaySchedule(paySchedule);
        setDueDate(addCalendarDays(todayStr, days));
      }

      setCustomerPoNumber(po.customerPoNumber || "");
      setAdditionalInfo(
        po.additionalInfo || 
        "Thank you for your business. Payment terms are subject to the Prompt Payment Act."
      );
    }
  }, [po, customer, isOpen]);

  if (!isOpen || !po) return null;

  // Pricing calculation
  const getPoMarkupRate = (p) => {
    if (p && p.markupRate !== undefined && p.markupRate !== null) {
      return parseFloat(p.markupRate);
    }
    const saved = localStorage.getItem("global_markup_rate");
    return saved !== null ? parseFloat(saved) : 15;
  };

  const isCcPayment = (p) => {
    return Boolean(p && p.paymentMethod && p.paymentMethod.trim().toLowerCase() === "credit card");
  };

  const getPoCcFee = () => {
    const saved = localStorage.getItem("global_cc_fee") || localStorage.getItem("global_non_cc_discount");
    return saved !== null ? parseFloat(saved) : 3;
  };

  const getEffectiveMarkupRate = (p) => {
    const base = getPoMarkupRate(p);
    if (isCcPayment(p)) {
      const ccFee = getPoCcFee();
      return base + ccFee;
    }
    return base;
  };

  const markupRate = getEffectiveMarkupRate(po);
  const estShipping = Number(po.estShippingCost || 0);
  const itemsCount = po.items?.length || 0;

  const calculateItemTotal = (item) => {
    if (item.unitPrice) return Number(item.unitPrice);
    const qty = Number(item.quantity || 0);
    const vPrice = Number(item.vendorPrice || 0);
    const sCost = Number(item.shippingCost || 0);
    const lineTotalCost = (qty * vPrice) + sCost;
    const lineClientTotal = lineTotalCost * (1 + markupRate / 100);
    const shippingPad = (estShipping > 0 && itemsCount > 0) ? (estShipping / itemsCount) : 0;
    return Number((lineClientTotal + shippingPad).toFixed(2));
  };

  const totalInvoiceAmount = (po.items || []).reduce((sum, item) => sum + calculateItemTotal(item), 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSendInvoice) {
      onSendInvoice({
        invoiceDate,
        dueDate,
        customerPoNumber,
        additionalInfo,
        totalAmount: totalInvoiceAmount
      });
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "800px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FileText size={20} style={{ color: "var(--primary)" }} />
            <h3 style={{ margin: 0 }}>Review & Send Invoice to QuickBooks</h3>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Target QuickBooks Account Indicator */}
          <div style={{
            backgroundColor: "rgba(16, 185, 129, 0.08)",
            border: "1px solid rgba(16, 185, 129, 0.3)",
            borderRadius: "6px",
            padding: "0.75rem 1rem",
            marginBottom: "1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Building size={18} style={{ color: "var(--success)" }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  Target QuickBooks Account: {company?.name || "Default Company"}
                  <span style={{ 
                    fontSize: "0.7rem", 
                    padding: "2px 6px", 
                    borderRadius: "4px", 
                    backgroundColor: (company?.qbEnvironment === "production") ? "rgba(59, 130, 246, 0.2)" : "rgba(245, 158, 11, 0.2)",
                    color: (company?.qbEnvironment === "production") ? "#60a5fa" : "#fbbf24",
                    border: `1px solid ${(company?.qbEnvironment === "production") ? "rgba(59, 130, 246, 0.4)" : "rgba(245, 158, 11, 0.4)"}`,
                    textTransform: "uppercase",
                    fontWeight: 700
                  }}>
                    {company?.qbEnvironment || "Sandbox"}
                  </span>
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                  {company?.qbCompanyId ? `QB Company ID: ${company.qbCompanyId}` : "Using configured QuickBooks OAuth credentials"}
                </div>
              </div>
            </div>
            <span className="badge badge-success" style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <CheckCircle2 size={12} /> QB Connected
            </span>
          </div>

          {/* Invoice Header Details */}
          <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div className="form-group">
              <label className="form-label">Invoice # (PO Number)</label>
              <input
                type="text"
                className="form-input"
                value={po.poNumber}
                disabled
                style={{ backgroundColor: "rgba(255, 255, 255, 0.05)", cursor: "not-allowed", fontWeight: 600 }}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="invCustomerPo">Customer PO # (Optional)</label>
              <input
                id="invCustomerPo"
                type="text"
                className="form-input"
                placeholder="e.g. CUST-PO-991"
                value={customerPoNumber}
                onChange={(e) => setCustomerPoNumber(e.target.value)}
              />
            </div>
          </div>

          <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="invDate">
                <Calendar size={14} style={{ display: "inline", marginRight: "4px" }} />
                Invoice Date *
              </label>
              <input
                id="invDate"
                type="date"
                required
                className="form-input"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="invDueDate">
                <Calendar size={14} style={{ display: "inline", marginRight: "4px" }} />
                Due Date * (Net Schedule: {customer?.paySchedule || "Net 30"})
              </label>
              <input
                id="invDueDate"
                type="date"
                required
                className="form-input"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Billing & Shipping Address Summary */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            backgroundColor: "var(--bg-card)",
            padding: "0.85rem",
            borderRadius: "6px",
            border: "1px solid var(--border-color)",
            marginBottom: "1.25rem",
            fontSize: "0.85rem"
          }}>
            <div>
              <strong style={{ color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>BILL TO:</strong>
              <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{po.clientName}</div>
              {customer?.billAddress ? (
                <div style={{ color: "var(--text-secondary)" }}>
                  {customer.billAddress}{customer.billAddress2 ? `, ${customer.billAddress2}` : ""}<br />
                  {customer.billCity}, {customer.billState} {customer.billZip}
                </div>
              ) : customer?.address ? (
                <div style={{ color: "var(--text-secondary)" }}>
                  {customer.address}{customer.address2 ? `, ${customer.address2}` : ""}<br />
                  {customer.city}, {customer.state} {customer.zip}
                </div>
              ) : (
                <div style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No billing address recorded</div>
              )}
            </div>

            <div>
              <strong style={{ color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>SHIP TO & MARK FOR:</strong>
              {po.shipToAddress ? (
                <div style={{ color: "var(--text-secondary)" }}>
                  {po.shipToAddress}{po.shipToAddress2 ? `, ${po.shipToAddress2}` : ""}<br />
                  {po.shipToCity}, {po.shipToState} {po.shipToZip}
                </div>
              ) : (
                <div style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No ship-to address specified</div>
              )}
              {po.markFor && (
                <div style={{ marginTop: "0.25rem", color: "var(--primary)", fontWeight: 600 }}>
                  Mark For (M/F): {po.markFor}
                </div>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" style={{ fontWeight: 600 }}>QuickBooks Line Items</label>
            <div className="table-responsive" style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: "6px" }}>
              <table className="proc-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th style={{ width: "80px" }}>SKU / Line #</th>
                    <th>Product / Service Description</th>
                    <th className="text-center" style={{ width: "70px" }}>Qty</th>
                    <th className="text-right" style={{ width: "100px" }}>Unit Rate</th>
                    <th className="text-right" style={{ width: "110px" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {po.items && po.items.map((item, idx) => {
                    const lineNum = item.lineNumber || String(idx + 1).padStart(2, "0");
                    const itemTotal = calculateItemTotal(item);
                    const qty = Number(item.quantity || 1);
                    const unitRate = qty > 0 ? (itemTotal / qty) : itemTotal;

                    return (
                      <tr key={idx}>
                        <td style={{ fontWeight: 600, color: "var(--primary)" }}>{lineNum}</td>
                        <td style={{ color: "var(--text-primary)" }}>{item.description || "Line Item"}</td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-right">${unitRate.toFixed(2)}</td>
                        <td className="text-right" style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                          ${itemTotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "1rem",
              padding: "0.75rem 1rem",
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              borderBottom: "1px solid var(--border-color)",
              fontSize: "1rem",
              fontWeight: 700
            }}>
              <span>Total Invoice Amount:</span>
              <span style={{ color: "var(--success)", fontSize: "1.1rem" }}>
                ${totalInvoiceAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* Note to Customer */}
          <div className="form-group" style={{ marginBottom: "1.5rem" }}>
            <label className="form-label" htmlFor="invNote">Note to Customer (Prompt Payment Act / Terms)</label>
            <textarea
              id="invNote"
              rows={3}
              className="form-input"
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder="Terms, memos, or payment instructions..."
              style={{ resize: "vertical" }}
            />
          </div>

          {/* Footer Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {loading ? <Loader2 className="spinner-icon" size={16} /> : <Send size={16} />}
              Send to QuickBooks
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}